// src/admin/screens/kyc/modals/RejectKycModal.tsx
// Marks the submission as rejected and writes the reviewer-facing reason
// into review_notes. The borrower app displays review_notes verbatim on
// the KYC tab so the user knows what to fix before resubmitting.
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import { useAuth } from '@admin/auth/useAuth';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';

const REASONS = [
  'Underage (under 18)',
  'NRC expired',
  'NRC not readable',
  'Suspected forgery',
  'Selfie mismatch',
  'Other',
] as const;

const schema = z.object({
  category: z.enum(REASONS),
  detail: z.string().min(10, 'At least 10 characters'),
});
type Values = z.infer<typeof schema>;

interface Props {
  kyc: { id: string; user_id: string };
  onClose: (refresh: boolean) => void;
}

export default function RejectKycModal({ kyc, onClose }: Props) {
  const { admin } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { category: 'Other' },
  });

  const submit = async (v: Values) => {
    if (!admin) return;
    setBusy(true);
    try {
      const reason = `${v.category}: ${v.detail}`;
      const { error } = await supabase
        .from('kyc_submissions')
        .update({
          status: 'rejected',
          reviewer_admin_id: admin.id,
          reviewed_at: new Date().toISOString(),
          review_notes: reason,
        })
        .eq('id', kyc.id);
      if (error) throw error;
      await recordAudit({
        action: 'kyc.reject',
        target_type: 'kyc',
        target_id: kyc.id,
        payload: { borrower_id: kyc.user_id, reason },
      });
      toast({
        kind: 'success',
        msg: 'KYC rejected. Borrower will see the reason in-app.',
      });
      await qc.invalidateQueries({ queryKey: ['kyc', kyc.id] });
      await qc.invalidateQueries({ queryKey: ['kyc-queue'] });
      onClose(true);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={() => onClose(false)} title="Reject KYC">
      <form
        onSubmit={handleSubmit(submit)}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <p className="small">
          The borrower sees the reason in-app — be specific so they can fix it.
        </p>
        <FormField
          label="Category"
          htmlFor="reject-kyc-category"
          error={errors.category?.message}
          required
        >
          <select id="reject-kyc-category" {...register('category')}>
            {REASONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </FormField>
        <FormField
          label="Detail"
          htmlFor="reject-kyc-detail"
          error={errors.detail?.message}
          required
        >
          <textarea id="reject-kyc-detail" rows={3} {...register('detail')} />
        </FormField>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={() => onClose(false)} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="danger" loading={busy}>
            Reject
          </Button>
        </div>
      </form>
    </Modal>
  );
}
